import React from 'react';
import { User } from 'lucide-react';
import { getDaysFromExpiry } from '../../lib/dateUtils';

// Digital License Card – mimics the official Malaysian physical driving licence layout.
export const LicenseCard = ({ result }) => {
  const days = getDaysFromExpiry(result.expiryDate);
  const isExpired = result.status === 'TAMAT TEMPOH';
  const activeClasses = result.classes.filter(c => c.status === 'AKTIF');
  const expiredClasses = result.classes.filter(c => c.status === 'TAMAT TEMPOH');

  return (
    <div
      data-testid="digital-license-card"
      className="rounded-2xl overflow-hidden shadow-xl border border-gray-200"
      style={{ fontFamily: 'Arial, sans-serif' }}
    >
      {/* Card Header - Blue Strip */}
      <div className="flex items-center justify-between px-4 py-2.5" style={{ backgroundColor: '#003580' }}>
        <div className="flex items-center gap-2">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Jabatan_Pengangkutan_Jalan.svg/120px-Jabatan_Pengangkutan_Jalan.svg.png"
            alt="JPJ"
            style={{ width: 32, height: 34, objectFit: 'contain' }}
          />
          <div className="text-white">
            <div className="font-black text-xs leading-tight tracking-wide">JABATAN PENGANGKUTAN JALAN</div>
            <div className="text-xs opacity-75 leading-tight">ROAD TRANSPORT DEPARTMENT MALAYSIA</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-white font-black text-sm tracking-widest">LESEN MEMANDU</div>
          <div className="text-white/70 text-xs">DRIVING LICENCE</div>
        </div>
      </div>

      {/* Card Body */}
      <div className="bg-white">
        <div className="flex">
          {/* Left - Photo + classes */}
          <div
            className="flex flex-col items-center justify-between p-3 border-r border-gray-200"
            style={{ minWidth: 100, backgroundColor: '#f8faff' }}
          >
            {/* Photo */}
            <div
              className="w-20 h-24 border-2 border-gray-300 rounded flex flex-col items-center justify-center mb-2 overflow-hidden"
              style={{ backgroundColor: '#e8edf5' }}
            >
              {result.photo ? (
                <img src={result.photo} alt="Gambar" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
              ) : (
                <>
                  <User size={32} className="text-gray-400 mb-1" />
                  <span className="text-gray-400 text-xs text-center leading-tight">GAMBAR<br/>PHOTO</span>
                </>
              )}
            </div>
            {/* License Classes */}
            <div>
              <div className="text-center text-xs font-bold text-gray-500 mb-1.5">KELAS</div>
              <div className="flex flex-wrap gap-1 justify-center">
                {activeClasses.map((cls, i) => (
                  <div
                    key={`a-${i}`}
                    className="w-8 h-8 rounded flex items-center justify-center font-black text-sm text-white"
                    style={{ backgroundColor: '#003580' }}
                  >
                    {cls.class}
                    {cls.endorsement && cls.endorsement !== '-' && (
                      <span className="text-yellow-300 ml-0.5" style={{ fontSize: '8px' }}>{cls.endorsement}</span>
                    )}
                  </div>
                ))}
                {expiredClasses.map((cls, i) => (
                  <div
                    key={`e-${i}`}
                    className="w-8 h-8 rounded flex items-center justify-center font-black text-sm text-gray-400 line-through"
                    style={{ backgroundColor: '#e5e7eb' }}
                  >
                    {cls.class}
                  </div>
                ))}
                {result.lesenVokasional && result.lesenVokasional.filter(v => v.status === 'AKTIF').map((vl) => (
                  <div
                    key={`v-${vl.noLesen}`}
                    className="w-8 h-8 rounded flex items-center justify-center font-black text-xs text-yellow-900"
                    style={{ backgroundColor: '#fbbf24' }}
                  >
                    {vl.jenis}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Personal Details */}
          <div className="flex-1 p-3">
            {/* Status badge */}
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}>
                {isExpired ? '● TAMAT TEMPOH' : '● AKTIF'}
              </span>
              {result.jenisPemegang === 'Vokasional' && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                  VOKASIONAL
                </span>
              )}
            </div>

            {/* Name */}
            <div className="mb-2 pb-2 border-b border-gray-100">
              <div className="text-xs text-gray-400 uppercase tracking-wide">Nama / Name</div>
              <div className="font-black text-sm text-gray-900 leading-tight" data-testid="license-card-name">{result.name}</div>
            </div>

            {/* IC + DOB */}
            <div className="grid grid-cols-2 gap-2 mb-2 pb-2 border-b border-gray-100">
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">No. Kad Pengenalan</div>
                <div className="font-bold text-xs text-gray-800 font-mono tracking-wider">{result.ic}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Tarikh Lahir</div>
                <div className="font-bold text-xs text-gray-800">{result.dob}</div>
              </div>
            </div>

            {/* License No */}
            <div className="mb-2 pb-2 border-b border-gray-100">
              <div className="text-xs text-gray-400 uppercase tracking-wide">No. Lesen / License No.</div>
              <div className="font-black text-sm text-gray-900 font-mono tracking-widest">{result.licenseNo}</div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Tarikh Keluaran</div>
                <div className="font-bold text-xs text-gray-800">{result.issueDate}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Tarikh Luput</div>
                <div className={`font-bold text-xs ${isExpired ? 'text-red-600' : 'text-gray-800'}`}>
                  {result.expiryDate}
                  {!isExpired && days <= 90 && days >= 0 && (
                    <span className="ml-1 text-orange-500 font-bold">({days}h)</span>
                  )}
                </div>
              </div>
            </div>

            {/* PSV detail if vocational */}
            {result.lesenVokasional && result.lesenVokasional.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                {result.lesenVokasional.filter(v => v.status === 'AKTIF').map((vl) => (
                  <div key={vl.noLesen} className="flex items-center gap-2 bg-yellow-50 rounded px-2 py-1">
                    <span className="font-black text-xs text-yellow-800 bg-yellow-400 px-1.5 py-0.5 rounded">{vl.jenis}</span>
                    <span className="text-xs text-yellow-700 font-medium">{vl.jenisKenderaan}</span>
                    <span className="text-xs text-yellow-600 ml-auto">Luput: {vl.expiryDate}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Card Footer - Dark strip with license number barcode style */}
        <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: '#00264d' }}>
          <div className="text-white/70 text-xs font-mono tracking-widest">{result.licenseNo}</div>
          <div className="flex gap-0.5">
            {Array.from({ length: 28 }).map((_, i) => (
              <div
                key={i}
                className="rounded-sm"
                style={{
                  width: i % 3 === 0 ? 3 : 1.5,
                  height: 18,
                  backgroundColor: `rgba(255,255,255,${0.3 + Math.random() * 0.7})`
                }}
              />
            ))}
          </div>
          <div className="text-white/70 text-xs">{result.negeriLesen}</div>
        </div>
      </div>
    </div>
  );
};

export default LicenseCard;
